import { getSessionId } from '@cloudpremise/portal_core';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE, persistReducer, persistStore } from 'redux-persist';
import storageSession from 'redux-persist/lib/storage/session';
import { encryptTransform } from './TransformEncrypt';
import { rtkQueryErrorGlobalHandler } from './middleware/apiErrorHandler';
import { caseReducer } from './reducers';


const sessionId = getSessionId();

const rootPersistConfig = {
  // base the key (used for the sessionStorage lookup) on part of the session id
  // in part because there is an issue if the keys don't match between sessions
  // so adding a bit of redundancy in case the 2 other measures fail to clear the store
  key: sessionId ? sessionId.slice(0, 10) : 'root',
  version: 1,
  storage: storageSession,
  blacklist: ['case'],
  transforms: [
    encryptTransform({
      secretKey: sessionId || 'no-session-id'
    })
  ]
};


const rootReducers = combineReducers({
    case: caseReducer,
});
  
const persistedReducer = persistReducer(rootPersistConfig, rootReducers);

export const setupStore = (preloadedState?: Partial<RootState>) =>
configureStore({
    reducer: sessionId ? persistedReducer : rootReducers,

    // show the devTools only in development
    devTools: process.env.NODE_ENV !== 'production',

    // Adding the api middleware enables caching, invalidation, polling,
    // and other useful features of `rtk-query`.
    middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
        serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
        }
    }).concat([rtkQueryErrorGlobalHandler]),
    preloadedState
});
  
export const store = setupStore();

export const persistor = sessionId ? persistStore(store) : null;

  // Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof rootReducers>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppStore = typeof store;
export type AppDispatch = AppStore['dispatch'];


// clear out the store and any session timeout info
export const clearPersistStorage = () => {
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith('persist')) {
        sessionStorage.removeItem(key);
      }
    });
  
    Object.keys(localStorage).forEach((key) => {
      if (key.match(/(keepalive|sid_)/i)) {
        localStorage.removeItem(key);
      }
    });
};